import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
// @mui
import {
  Stack,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  Box,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider } from '../../../components/hook-form';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';
// utils
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

const JobChangeInterestSchema = Yup.object().shape({
  is_interested_in_job_change: Yup.string().required('이직 관심 여부를 선택해주세요'),
  looking_for: Yup.string().max(500, '500자 이내로 입력해주세요'),
});

export default function JobChangeInterestDialog({ isOpen, closeDialog }) {
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const [isLoading, setIsLoading] = useState(false);

  const defaultValues = {
    is_interested_in_job_change: '',
    looking_for: '',
  };

  const methods = useForm({
    resolver: yupResolver(JobChangeInterestSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, dirtyFields },
  } = methods;

  // 초기 데이터 로드
  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/crews/job-change-interest/');
      const data = response.data;
      
      // 폼 초기값 설정
      reset({
        is_interested_in_job_change: data.is_interested_in_job_change?.toString() || '',
        looking_for: data.looking_for || '',
      });
    } catch (error) {
      console.error('Failed to fetch job change interest data:', error);
      enqueueSnackbar('데이터를 불러오는데 실패했습니다', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  const onSubmit = async (data) => {
    try {
      // 서버는 전달된 키만 갱신하므로, 변경되지 않은 필드는 아예 보내지 않는다
      // (빈 값이라도 보내면 변경 없는 저장에도 슬랙 알림이 발송됨)
      const submitData = {
        is_interested_in_job_change: data.is_interested_in_job_change === 'true',
      };
      if (dirtyFields.looking_for) {
        submitData.looking_for = data.looking_for || '';
      }

      await axios.post('/api/crews/job-change-interest/', submitData);
      enqueueSnackbar('설정이 저장되었습니다', { variant: 'success' });
      closeDialog();
    } catch (error) {
      console.error('Failed to submit job change interest:', error);
      enqueueSnackbar('저장에 실패했습니다', { variant: 'error' });
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    closeDialog();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant={smUp ? "h5" : "subtitle1"}>
            이직 기회 알아보기
          </Typography>
          <IconButton edge="end" onClick={handleClose}>
            <Iconify icon="eva:close-fill" sx={{ color: "text.primary", width: 24, height: 24 }} />
          </IconButton>
        </Stack>

        {isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography>데이터를 불러오는 중...</Typography>
          </Box>
        ) : (
          <FormProvider methods={methods}>
            <Stack spacing={3}>
              {/* 1. 이직 관심 여부 */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  현재 이직에 관심이 있으신가요?
                </Typography>
                <Controller
                  name="is_interested_in_job_change"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        label="네, 관심 있어요"
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        label="아니요"
                      />
                    </RadioGroup>
                    {error && (<Typography variant="caption" color="error">{error.message}</Typography>)}
                    </>
                  )}
                />
              </Box>

              {/* TODO: 정규직 공고 오픈 후 주석해제 (watch('is_interested_in_job_change') === 'true' 일 때만 노출) */}
              {/* <Box sx={{ px: 2, py: 1.5, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04), borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ minWidth: 22, minHeight: 22, width: 22, height: 22, color: "text.secondary" }} />
                  <Typography variant="body2">
                    정규직 공고 메뉴에서 채용 공고를 확인하고 직접 지원할 수도 있습니다
                  </Typography>
                </Stack>
              </Box> */}

              {/* 2. 찾고 있는 곳 설명 */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  어떤 곳을 찾고 계신지 알려주세요 (선택사항)
                </Typography>
                <Controller
                  name="looking_for"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="예: 핀테크 스타트업, 백엔드 개발자 포지션 등..."
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          fontSize: '14px',
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Stack>
          </FormProvider>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
        <Button 
          onClick={handleClose} 
          size="large"
          sx={{ mr: 1 }}
        >
          취소
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          size="large"
          loading={isSubmitting}
          disabled={isLoading}
          sx={{ px: 4 }}
        >
          저장하기
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}